import { useParams, Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = articles
    .filter((a) => a.categorySlug === article?.categorySlug && a.slug !== slug)
    .slice(0, 3);

  if (!article) {
    return (
      <PageLayout>
        <div className="flex flex-col items-center justify-center py-32">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 mb-4">
            <i className="ri-file-search-line text-2xl text-foreground-400"></i>
          </div>
          <h2 className="text-xl font-semibold text-foreground-800 mb-2">文章未找到</h2>
          <p className="text-foreground-500 mb-6">该文章不存在或已被移除</p>
          <Link
            to="/"
            className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
        <BackToTop />
      </PageLayout>
    );
  }

  const currentCategory = categories.find((c) => c.slug === article.categorySlug);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '十八禁合规指引平台',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://hyel.com.cn/article/${article.slug}`,
    },
  };

  return (
    <PageLayout>
      <title>{`${article.title} | 十八禁合规指引平台`}</title>
      <meta name="description" content={article.excerpt} />
      <meta name="keywords" content={article.tags.join(',')} />
      <link rel="canonical" href={`https://hyel.com.cn/article/${article.slug}`} />
      <meta property="og:title" content={`${article.title} | 十八禁合规指引平台`} />
      <meta property="og:description" content={article.excerpt} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`https://hyel.com.cn/article/${article.slug}`} />

      <SchemaOrg data={articleSchema} />

      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: article.category, href: `/category/${article.categorySlug}` },
          { label: article.title },
        ]}
      />

      <article className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <header className="mb-8">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <Link
                to={`/category/${article.categorySlug}`}
                className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700 hover:bg-accent-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                {article.category}
              </Link>
              <span className="text-xs text-foreground-400">{article.date}</span>
              <span className="text-xs text-foreground-400">{article.readTime}</span>
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
              {article.excerpt}
            </p>
            <div className="flex items-center gap-3 mt-4 pt-4 border-t border-background-200/70">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-sm font-medium">
                {article.author.charAt(0)}
              </div>
              <span className="text-sm text-foreground-600 font-medium">
                {article.author}
              </span>
            </div>
          </header>

          {/* Content */}
          <div
            className="prose prose-sm md:prose-base max-w-none
              prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-semibold
              prose-p:text-foreground-700 prose-p:leading-relaxed
              prose-h3:text-lg prose-h3:md:text-xl prose-h3:mt-8 prose-h3:mb-4
              prose-p:mb-4
              prose-strong:text-foreground-800
              [&_p]:text-sm [&_p]:md:text-base"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Tags */}
          <div className="flex items-center flex-wrap gap-2 mt-10 pt-6 border-t border-background-200/70">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 whitespace-nowrap"
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h3 className="text-xl font-semibold font-heading text-foreground-900 mb-6">
              相关文章
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.slug}
                  to={`/article/${ra.slug}`}
                  className="block p-4 md:p-5 rounded-xl bg-background-50 border border-background-200/70 transition-all duration-300 hover:border-primary-300 cursor-pointer"
                >
                  <h4 className="text-sm font-semibold font-heading text-foreground-900 mb-2 line-clamp-2">
                    {ra.title}
                  </h4>
                  <p className="text-xs text-foreground-500 line-clamp-2">
                    {ra.excerpt}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <BackToTop />
    </PageLayout>
  );
}